import React, { useState, useEffect } from "react";
import classnames from 'classnames';
import './SimulatorConfigEditor.scss';
import DashboardContent from "@/components/dashboard/DashboardContent";
import { SimulatorBuilder } from "@/views/simulators";
import {ByzzerButton} from "@byzzer/ui-components";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { SimulatorConfig, SimulatorInsightFilters, SimulatorRunConfig, ScenarioFilterRunConfigOptions } from "@/types/SimulatorTypes";
import { useUser } from "@/contexts/UserContext";
import {alert as ByzzerAlert} from '@/components/form';
import { PRESET_DEFAULT_FILTER_VALUES } from "@/views/simulators/SimulatorBuilder/simulatorPresetsForTesting";
import { useSimulatorRunService } from "@/services/simulatorRun.service";
import { getProductBySku } from "@/services/product.service";
import { useTenantApi } from "@/hooks";
import { useMarketService } from "@/services/market.service";

export type SimulatorConfigEditorProps = {
    data?: any;
}

export type ScenarioSimulatorConfigOptions = {
    sku?: string;
    filterEditor: {
        filterConfigOptions: ScenarioFilterRunConfigOptions;
    };
    simulatorConfig: SimulatorConfig;
}

type SavedSimulatorRunConfig = SimulatorInsightFilters;

export const emptyFilterRunConfig: SimulatorRunConfig = {
    categories: [],
    brands: [],
    ppgId: null,
    ppgGroupNames: [],
    timePeriod: { type: 'relative' }
}

export const SimulatorConfigEditor = ({
    ...props
}: SimulatorConfigEditorProps) => {

    const baseClassName = 'byz-simulator-config-editor';
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { allProducts, subscribedSkus, features, defaultRunConfig } = useUser();
    const { getSimulatorDefaultRunConfigBySku } = useSimulatorRunService();
    const { getSimulatorRunHistoryById } = useTenantApi();
    const { getRunConfigMarketsByKeys, getRunConfigMarketForMarketName } = useMarketService();
    const [simulatorConfigOptions, setSimulatorConfigOptions] = useState<ScenarioSimulatorConfigOptions>();
    const [defaultFilterRunConfig, setDefaultFilterRunConfig] = useState<SimulatorRunConfig>(emptyFilterRunConfig);
    const [simulator, setSimulator] = useState<ProductWithMeta>();

    const sku = searchParams.get('sku') as string;
    const datasetSku = searchParams.get('datasetSku');
    const runId = searchParams.get('runId');

    useEffect(() => {
        ;(async () => {
            if (await validateOrExit()) {
                await loadConfig();
            }
        })();
    }, [sku, allProducts, runId]);

    async function validateOrExit(): Promise<boolean> {
        if (!subscribedSkus.includes(sku) && !features?.enableSimulatorAccess && !simulatorConfigOptions) {
            await ByzzerAlert({
                // @ts-ignore
                title: 'Product Not Available',
                content: 'The product you selected is not available in your current subscription.',
            });
            navigate(-1);
            return false;
        }
        return true;
    }

    const editingSavedScenario = Boolean(runId);

    function mapSavedRunConfigToFilterRunConfig(runConfig?: SavedSimulatorRunConfig): SimulatorRunConfig {
        const savedRunConfig = runConfig ?? {};
        const categories = savedRunConfig.categoryNames ?? [];
        const marketKeys = Array.isArray(savedRunConfig.marketKeys) ? savedRunConfig.marketKeys.filter(Boolean) : [];
        const marketsFromKeys = marketKeys.length ? getRunConfigMarketsByKeys(marketKeys) : [];
        const fallbackMarketNames = Array.isArray(savedRunConfig.marketNames)
            ? savedRunConfig.marketNames.filter(Boolean)
            : savedRunConfig.marketName
            ? [savedRunConfig.marketName]
            : [];
        const fallbackMarkets = marketsFromKeys.length
            ? []
            : fallbackMarketNames
                  .map((marketName) => getRunConfigMarketForMarketName(marketName, categories))
                  .filter(Boolean);

        return {
            categories,
            brands: savedRunConfig.brandNames ?? [],
            ppgId: savedRunConfig.ppgId ? Number(savedRunConfig.ppgId) : null,
            ppgDefinitionName: savedRunConfig.ppgDefinitionName ?? null,
            ppgGroupNames: savedRunConfig.ppgGroupName ? [savedRunConfig.ppgGroupName] : [],
            markets: (marketsFromKeys.length ? marketsFromKeys : fallbackMarkets) as SimulatorRunConfig['markets'],
            timePeriod: savedRunConfig.timePeriod ?? { type: 'relative' }
        };
    }

    async function loadConfig(): Promise<void> {
        try {
            const simulator = allProducts.find((p) => p.sku === sku) ?? getProductBySku(sku);

            if (!simulator) {
                await ByzzerAlert({
                    // @ts-ignore
                    title: 'Product Not Found',
                    content: `We were unable to find the product you are trying to configure.`,
                });

                return navigate(-1);
            }

            setSimulator(simulator);

            const nextSimulatorConfigOptions = {
                filterEditor: {
                    filterConfigOptions: simulator?.metadata?.configOptions,
                },
                simulatorConfig: {
                    ...simulator?.metadata.simulatorConfig,
                    dataset: simulator?.metadata.datasetConfig
                }
            };

            if (editingSavedScenario) {
                let savedRunConfig = (location.state as any)?.runConfig;
                if (!savedRunConfig) {
                    const runIdNum = Number(runId);
                    if (!Number.isFinite(runIdNum)) {
                        await ByzzerAlert({
                            // @ts-ignore
                            title: 'Simulation Not Found',
                            content: 'We were unable to load the saved simulation. Invalid run ID.',
                        });
                        navigate(-1);
                        return;
                    }
                    savedRunConfig = (await getSimulatorRunHistoryById(runIdNum)).runConfig;
                }
                setDefaultFilterRunConfig(mapSavedRunConfigToFilterRunConfig(savedRunConfig));
                setSimulatorConfigOptions(nextSimulatorConfigOptions);
            } else {
                const defaultSimulatorRunConfig = getSimulatorDefaultRunConfigBySku(sku!);

                setDefaultFilterRunConfig(PRESET_DEFAULT_FILTER_VALUES ?? defaultSimulatorRunConfig)
                setSimulatorConfigOptions(nextSimulatorConfigOptions);
            }
        } catch (error) {
            console.error(`SimulatorConfigEditor - loadConfig fired - 'error' ===>>`, error);
            if (editingSavedScenario) {
                await ByzzerAlert({
                    // @ts-ignore
                    title: 'Simulation Not Found',
                    content: 'We were unable to load the saved simulation. It may have been deleted.',
                });
                navigate(-1);
            }
        }
    }


    return Boolean(simulator && simulatorConfigOptions) && (
        // @ts-ignore
        <DashboardContent
            title={<>Design Your {simulator?.title} Simulator</>}
            className={`${baseClassName}__container`}
            // loading={!runConfigOptions}
            subTitle={
                <span>
                    {/* tbd */}
                </span>
            }
            extras={(
                <div className={`${baseClassName}__header-actions`}>
                    <ByzzerButton type={'negative'} label={'Cancel'} onClick={() => navigate(-1)}/>
                </div>
            )}
        >
            <SimulatorBuilder
                sku={sku!}
                datasetSku={(datasetSku ?? simulator?.metadata?.datasetSku)!}
                defaultFilterRunConfig={defaultFilterRunConfig}
                title={simulator?.title}
                simulatorConfigOptions={simulatorConfigOptions!}
            />
        </DashboardContent>
    );

};

export default SimulatorConfigEditor;

SimulatorConfigEditor.displayName = 'SimulatorConfigEditor';